import React, { useEffect, useState } from 'react';
import { ListChecks } from 'lucide-react';
import {
  DonateFeatureState,
  DonateFeatureStatus,
  loadDonateFeatureStatus,
} from '../../../components/donate-kinds/donate-kinds';

/**
 * Bảng "Các kiểu donate bạn nhận" ở tab Chung của Cài đặt Donate: một chỗ thấy hết kiểu nào đang bật
 * (người xem thấy trên trang của bạn) và kiểu nào đang tắt. Nút ở mỗi dòng chuyển sang đúng tab có
 * công tắc của kiểu đó. Không bật tắt ngay tại đây vì mỗi kiểu cần cài thêm (giá, ô vòng quay, câu hỏi
 * bình chọn) mới chạy đúng.
 */

const DESC: Record<string, string> = {
  'muc-tieu': 'Thanh tiến độ gom Ca trên trang của bạn và trên live',
  nhac: 'Người xem trả Ca để gửi nhạc, bạn duyệt rồi phát trên live',
  'hoi-ai': 'Người xem trả Ca đặt câu hỏi, AI trả lời ngay trên live',
  'hieu-ung': 'Người xem trả Ca bấm hiệu ứng hiện trên live hoặc thầu khung tên',
  'binh-chon': 'Người xem donate để chọn phương án bạn đưa ra',
  'vong-quay': 'Người xem trả Ca quay trúng nhiệm vụ bạn sẽ làm',
  'hoi-vien': 'Người xem trả Ca hằng tháng để làm hội viên kênh của bạn',
};

const PILL: Record<DonateFeatureState, { text: string; cls: string }> = {
  on: { text: 'Đang bật', cls: 'bg-[#EAF7F0] text-[#11683F]' },
  off: { text: 'Đang tắt', cls: 'bg-[#F7F9FC] text-[#627184]' },
  'needs-obs': { text: 'Chờ mở OBS', cls: 'bg-[#FFF8E6] text-[#9A630C]' },
};

const DonateKindsBoard: React.FC<{ userId?: string; onGo: (muc: string) => void }> = ({ userId, onGo }) => {
  const [items, setItems] = useState<DonateFeatureStatus[] | null>(null);

  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    loadDonateFeatureStatus(userId).then((r) => alive && setItems(r));
    return () => {
      alive = false;
    };
  }, [userId]);

  if (!items) return null;
  const onCount = items.filter((x) => x.state === 'on').length;

  return (
    <div className="border rounded-xl border-[#DEE0E6] dark:border-slate-700 mt-[10px]" data-testid="donate-kinds-board">
      <p className="p-3 flex items-center gap-2 w-full text-[#18191E] dark:text-white font-semibold text-[22px] leading-[27px]">
        <ListChecks size={20} className="text-blue-600" />
        Các kiểu donate bạn nhận
      </p>
      <div className="w-full flex flex-col gap-2 bg-[#f6f7f8] dark:bg-dark2 p-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Donate thường luôn bật. Gửi nhạc, Hỏi AI, Hiệu ứng, Bình chọn và Vòng quay khi bật sẽ hiện ngay
          cạnh nút Donate trên trang của bạn và thành tab trong hộp donate. Mục tiêu donate hiện ở tab Donate
          trên trang của bạn, Hội viên kênh có nút tham gia riêng. Bạn đang bật {onCount} trên {items.length} kiểu.
        </p>
        <ul className="flex flex-col gap-2">
          {items.map((x) => (
            <li
              key={x.key}
              className="flex items-center gap-3 rounded-lg bg-white p-3 dark:bg-slate-800"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-[#121C2A] dark:text-white">{x.label}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PILL[x.state].cls}`}>
                    {PILL[x.state].text}
                  </span>
                </div>
                <p className="text-xs text-[#627184] dark:text-slate-400">{DESC[x.key]}</p>
              </div>
              <button
                type="button"
                onClick={() => onGo(x.muc)}
                className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium ${
                  x.state === 'off'
                    ? 'bg-[#2271B1] text-white hover:bg-[#215C91]'
                    : 'bg-[#EFF7FD] text-[#215C91] hover:bg-[#DCEEFB]'
                }`}
              >
                {x.state === 'off' ? 'Bật' : 'Cài đặt'}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default DonateKindsBoard;
